import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import axios from "axios";
import { useDashboard } from "../context/DashboardProvider";
import useMediaQuery from "@mui/material/useMediaQuery";

const OPENAI_API_KEY = process.env.AVA_OPENAI_API_KEY;

const AnalyzeData = () => {
  const { selectedArticles, startDate, endDate, frequency, data } = useDashboard();
  const theme = useTheme();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setAnalysis(null);
  }, [selectedArticles, startDate, endDate, frequency, data]);

  const handleAnalyze = async () => {
  if (!data || data.length === 0) {
    setAnalysis("No data available for analysis.");
    return;
  }

  setLoading(true);
  setAnalysis(null);

  try {
    console.log("📤 Sending Data to Backend:", data);

    const systemMessage = {
      role: "system",
      content: `You are a wine data science expert evaluating Wikipedia pageview data for US AVAs.
      Your goal is to analyze the following dataset and generate structured insights as bullet lists.`,
    };

    const userMessage = {
      role: "user",
      content: `Analyze the following Wikipedia pageview data:\n\n${JSON.stringify(data, null, 2)}

      Return your analysis in this exact structured format, with each containing a bullet list. Each bullet should NOT have a title:
      
      **Overall Trends**: Bullets (-) summarizing general pageview trends over time. No bold in the body text.
      **Anomalies**: Bullets (-) explaining any significant spikes, drops, or irregular patterns. No bold in the body text.
      **Comparative Insights**: Bullets (-) comparing and contrasting AVA pageviews if multiple AVAs are present. No bold in the body text.
      **Potential Influencing Factors**: Bullets (-) suggesting any external events, seasonality, or marketing efforts that may explain changes. No bold in the body text.
      **Actionable Recommendations**: Bullets (-) providing insights that could help stakeholders make data-driven decisions. No bold in the body text.
      **Potential Further Analysis**: Bullets (-) listing models to consider and how they could help someone looking to drive interest in the AVAs. No bold in the body text.

      Ensure your response follows the **above structure** exactly.`,
    };

    const response = await axios.post("/api/analyze", {
      model: "gpt-4o-mini-2024-07-18",
      messages: [systemMessage, userMessage],
      max_tokens: 3500,
      temperature: 0.5,
    });

    if (!response.data.choices || !response.data.choices[0].message) {
      throw new Error("Unexpected API response format.");
    }

    const gptMessage = response.data.choices[0].message.content.trim();
    setAnalysis(gptMessage);
  } catch (error) {
    console.error("❌ Backend API Request Failed:", error);
    setAnalysis(`Error: ${error.response?.data?.error?.message || error.message}`);
  } finally {
    setLoading(false);
  }
};


  const parseAnalysis = (text) => {
    const sections = text.split(/\*\*(.*?)\*\*/g).slice(1); // Splitting by bold headers
    let parsedData = [];

    for (let i = 0; i < sections.length; i += 2) {
      let title = sections[i].trim().replace(/[:\s]+$/, ""); // Remove trailing colons and spaces
      let content = sections[i + 1] ? sections[i + 1].trim().replace(/^:\s*/, "") : ""; // Remove leading colon if present
      
      // Convert dash-based bullets into proper list formatting
      let formattedContent = content
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line, index) =>
          line.startsWith("-")
            ? `<li key="${index}">${line.replace(/^-/, "").trim()}</li>`
            : `<p key="${index}">${line}</p>`
        )
        .join("");

      parsedData.push({ title, formattedContent });
    }
    return parsedData;
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: `0px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: "#121212 !important",
        marginLeft: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "40px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "auto",
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={32} sx={{ marginTop: 3, color: "#D4B9DB" }} />
          </>
        ) : analysis ? (
          <Grid container spacing={0}>
            {parseAnalysis(analysis).map((section, index) => (
              <Grid
                item
                xs={12}
                md={6}
                key={index}
                sx={{
                  borderRight: isMobile || index % 2 !== 0 ? "none" : "1px solid rgba(255, 255, 255, 0.2)",
                  borderBottom: index < parseAnalysis(analysis).length - 2 ? "1px solid rgba(255, 255, 255, 0.2)" : "none",
                  backgroundColor: "#121212",
                  marginLeft: 0,
                }}
              >
                <Card sx={{ height: "100%", boxShadow: "none" }}>
                  <CardContent sx={{ height: "100%", padding: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: theme.typography.datePicker,
                        fontWeight: "bold",
                      }}
                    >
                      {section.title}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: theme.typography.button.fontSize,
                        whiteSpace: "pre-wrap",
                        padding: 1,
                      }}
                      dangerouslySetInnerHTML={{ __html: `<ul>${section.formattedContent}</ul>` }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Button sx={{ marginTop: 3 }} variant="contained" onClick={handleAnalyze}>
            Analyze
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AnalyzeData;
